import React, { useState, useEffect } from 'react';
import { LoadingSpinner, Pagination } from '../../components';
import Layout from '../../components/Layout';
import api from '../../services/api';

// ========================================
// TYPE DEFINITIONS
// ========================================

interface StrayyoImageReport {
  id: string;
  reason: string;
  description: string | null;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  image: {
    id: string;
    url: string;
    caption: string | null;
    strayyo: {
      id: string;
      uid: string;
      name: string | null;
      type: string;
      area: string | null;
    };
  };
  user: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
  };
}

interface AdoptionReport {
  id: string;
  reason: string;
  description: string | null;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  post: {
    id: string;
    imageUrl: string;
    caption: string | null;
    status: string;
    createdAt: string;
    user: {
      id: string;
      name: string | null;
      email: string | null;
    };
  };
  user: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
  };
}

interface ReportStats {
  total: number;
  pending: number;
  reviewed?: number;
  resolved: number;
  byReason: Record<string, number>;
}

// ========================================
// MAIN COMPONENT
// ========================================

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<'strayyo' | 'adoption'>('strayyo');

  return (
    <Layout currentPage="reports">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="mt-2 text-gray-600">Review and moderate reported content</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('strayyo')}
              className={`${
                activeTab === 'strayyo'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Strayyo Image Reports
            </button>
            <button
              onClick={() => setActiveTab('adoption')}
              className={`${
                activeTab === 'adoption'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Adoption Post Reports
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'strayyo' ? <StrayyoImageReportsTab /> : <AdoptionReportsTab />}
      </div>
    </Layout>
  );
}

// ========================================
// STRAYYO IMAGE REPORTS TAB
// ========================================

function StrayyoImageReportsTab() {
  const [reports, setReports] = useState<StrayyoImageReport[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, statusFilter, reasonFilter, itemsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (reasonFilter) params.append('reason', reasonFilter);

      const [reportsResponse, statsResponse] = await Promise.all([
        api.get(`/admin/reports/images?${params}`),
        api.get(`/admin/reports/images/stats/summary`),
      ]);

      setReports(reportsResponse.data.reports);
      setTotalPages(reportsResponse.data.pagination.pages);
      setTotalItems(reportsResponse.data.pagination.total);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching image reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewReport = async (reportId: string, action: 'dismiss' | 'remove_image') => {
    const confirmMessage =
      action === 'remove_image'
        ? 'Are you sure you want to remove this image? This action cannot be undone.'
        : 'Are you sure you want to dismiss this report?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.put(`/admin/reports/images/${reportId}/review`, { action });
      alert(action === 'remove_image' ? 'Image removed successfully' : 'Report dismissed');
      fetchData();
    } catch (error) {
      console.error('Error reviewing report:', error);
      alert('Failed to process report');
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      inappropriate: 'Inappropriate Content',
      wrong_animal: 'Wrong Animal',
      poor_quality: 'Poor Quality',
      spam: 'Spam',
      other: 'Other',
    };
    return labels[reason] || reason;
  };

  if (loading && !reports.length) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Reports</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-red-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Resolved</div>
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Inappropriate</div>
            <div className="text-2xl font-bold text-orange-600">
              {stats.byReason.inappropriate || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Wrong Animal</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats.byReason.wrong_animal || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="RESOLVED">Resolved</option>
        </select>

        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Reasons</option>
          <option value="inappropriate">Inappropriate Content</option>
          <option value="wrong_animal">Wrong Animal</option>
          <option value="poor_quality">Poor Quality</option>
          <option value="spam">Spam</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
        {reports.map((report) => (
          <div key={report.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div 
              className="relative h-48 bg-gray-200 cursor-pointer"
              onClick={() => setSelectedImage(report.image.url)}
            >
              <img
                src={report.image.url}
                alt="Reported image"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    report.status === 'PENDING'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {report.status}
                </span>
              </div>
              <div className="absolute top-2 left-2">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                  {getReasonLabel(report.reason)}
                </span>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-2">
                <div className="font-medium text-gray-900">
                  {report.image.strayyo.name || 'Unnamed Strayyo'}
                </div>
                <div className="text-sm text-gray-500">{report.image.strayyo.uid}</div>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                <div className="font-medium">Reported by:</div>
                <div>{report.user.name || 'Anonymous'}</div>
                <div className="text-xs">{report.user.email || report.user.phone}</div>
              </div>

              {report.description && (
                <div className="text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded">
                  <div className="font-medium mb-1">Description:</div>
                  <div className="text-xs">{report.description}</div>
                </div>
              )}

              <div className="text-xs text-gray-400 mb-3">
                {new Date(report.createdAt).toLocaleDateString()}{' '}
                {new Date(report.createdAt).toLocaleTimeString()}
              </div>

              {report.status === 'PENDING' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleReviewReport(report.id, 'dismiss')}
                    className="flex-1 px-3 py-2 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleReviewReport(report.id, 'remove_image')}
                    className="flex-1 px-3 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Remove Image
                  </button>
                </div>
              )}

              {report.status === 'RESOLVED' && report.reviewedAt && (
                <div className="text-xs text-gray-500">
                  Resolved: {new Date(report.reviewedAt).toLocaleDateString()}
                  {report.reviewedBy && (
                    <div className="mt-1 italic">{report.reviewedBy}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setItemsPerPage}
      />

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-screen p-4">
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}

// ========================================
// ADOPTION REPORTS TAB
// ========================================

function AdoptionReportsTab() {
  const [reports, setReports] = useState<AdoptionReport[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, statusFilter, reasonFilter, itemsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (reasonFilter) params.append('reason', reasonFilter);

      const [reportsResponse, statsResponse] = await Promise.all([
        api.get(`/admin/reports/adoptions?${params}`),
        api.get(`/admin/reports/adoptions/stats/summary`),
      ]);

      setReports(reportsResponse.data.reports);
      setTotalPages(reportsResponse.data.pagination.pages);
      setTotalItems(reportsResponse.data.pagination.total);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching adoption reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewReport = async (reportId: string, action: 'dismiss' | 'remove_post') => {
    const confirmMessage =
      action === 'remove_post'
        ? 'Are you sure you want to remove this adoption post? This action cannot be undone.'
        : 'Are you sure you want to dismiss this report?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.put(`/admin/reports/adoptions/${reportId}/review`, { action });
      alert(action === 'remove_post' ? 'Post removed successfully' : 'Report dismissed');
      fetchData();
    } catch (error) {
      console.error('Error reviewing report:', error);
      alert('Failed to process report');
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      inappropriate: 'Inappropriate Content',
      spam: 'Spam',
      misleading: 'Misleading Information',
      scam: 'Potential Scam',
      other: 'Other',
    };
    return labels[reason] || reason;
  };

  if (loading && !reports.length) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Reports</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-red-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Reviewed</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.reviewed || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Resolved</div>
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Inappropriate</div>
            <div className="text-2xl font-bold text-orange-600">
              {stats.byReason.inappropriate || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="REVIEWED">Reviewed</option>
          <option value="RESOLVED">Resolved</option>
        </select>

        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Reasons</option>
          <option value="inappropriate">Inappropriate Content</option>
          <option value="spam">Spam</option>
          <option value="misleading">Misleading Information</option>
          <option value="scam">Potential Scam</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
        {reports.map((report) => (
          <div key={report.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div 
              className="relative h-48 bg-gray-200 cursor-pointer"
              onClick={() => setSelectedImage(report.post.imageUrl)}
            >
              <img
                src={report.post.imageUrl}
                alt="Reported adoption post"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    report.status === 'PENDING'
                      ? 'bg-red-100 text-red-800'
                      : report.status === 'REVIEWED'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {report.status}
                </span>
              </div>
              <div className="absolute top-2 left-2">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                  {getReasonLabel(report.reason)}
                </span>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-2">
                <div className="font-medium text-gray-900">Adoption Post</div>
                {report.post.caption && (
                  <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {report.post.caption}
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-600 mb-2">
                <div className="font-medium">Posted by:</div>
                <div>{report.post.user.name || 'Anonymous'}</div>
                <div className="text-xs">{report.post.user.email}</div>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                <div className="font-medium">Reported by:</div>
                <div>{report.user.name || 'Anonymous'}</div>
                <div className="text-xs">{report.user.email || report.user.phone}</div>
              </div>

              {report.description && (
                <div className="text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded">
                  <div className="font-medium mb-1">Description:</div>
                  <div className="text-xs">{report.description}</div>
                </div>
              )}

              <div className="text-xs text-gray-400 mb-3">
                {new Date(report.createdAt).toLocaleDateString()}{' '}
                {new Date(report.createdAt).toLocaleTimeString()}
              </div>

              {report.status === 'PENDING' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleReviewReport(report.id, 'dismiss')}
                    className="flex-1 px-3 py-2 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleReviewReport(report.id, 'remove_post')}
                    className="flex-1 px-3 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Remove Post
                  </button>
                </div>
              )}

              {report.status === 'RESOLVED' && report.reviewedAt && (
                <div className="text-xs text-gray-500">
                  Resolved: {new Date(report.reviewedAt).toLocaleDateString()}
                  {report.reviewedBy && (
                    <div className="mt-1 italic">{report.reviewedBy}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setItemsPerPage}
      />

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-screen p-4">
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}
